// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The other people in the arcade (M8-04, M8-05).
//
// 06 §4 asks for two kinds and they are deliberately not the same thing:
//
//   * **A headset player is a head and two hands**, driven by `pose-v1` at 15 Hz. They are
//     actually there — leaning over the glass, hands on the flipper buttons — and the pose stream
//     can say so.
//   * **A browser player is a glowing orb with a nametag at their bay.** A windowed browser has no
//     head tracking and never will, so there is no pose to draw. An orb is honest about that; a
//     head-and-hands avatar driven by nothing would have to invent a posture, and an invented
//     posture is worse than an abstraction because it looks like information.
//
// **Nothing here decides who is present.** It is handed a `VenuePort` and draws what it says.
// That seam is FR-030's: the arcade is on the front door and this module is reached from
// `XRRuntime`, so it may not import a connection — and it is also what makes "does a player fade
// out and stop being drawn" a question a test can answer instead of one needing two headsets.
//
// **Everything is generated, as everything in this product is.** A head is a sphere and a visor;
// the visor is not decoration, it is what makes a ball into a face and says which way somebody is
// looking. It is also what they are literally wearing.

import {
  AdditiveBlending,
  BoxGeometry,
  CanvasTexture,
  Color,
  Group,
  IcosahedronGeometry,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  SphereGeometry,
  type BufferGeometry,
  type Material,
} from 'three'
import { poseInVenue, type AvatarPose, type DrawnPose } from '../net/poseStream'
import { posesAreDrawable, type VenuePresence } from '../net/venuePresence'
import { isEmbodied } from '../net/platformSwitch'
import type { VenuePort } from '../xr/venuePort'
import type { Bay } from '../xr/arcadeBays'

export interface AvatarsBuild {
  readonly group: Group
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  readonly textures: readonly CanvasTexture[]
  /**
   * Draw everybody, with `seconds` of elapsed session time for the idle motion.
   *
   * Cheap to call every frame and correct to call with no port: with nobody connected the arcade
   * is simply empty, which is what an anonymous visitor and an offline build both get.
   */
  update: (port: VenuePort | undefined, seconds: number) => void
  dispose: () => void
}

/** Head radius, metres. A human head is about 0.10 across the temples; this is a little generous. */
const HEAD_RADIUS = 0.115

/** The nametag hangs this far above the orb, metres. */
const TAG_HEIGHT = 0.42

/** Where a browser player's orb floats, metres above the floor — about where a head would be. */
const ORB_HEIGHT = 1.5

const ORB_RADIUS = 0.14

/** How far the orb drifts up and down, metres, and how long a full cycle takes, seconds. */
const BOB = 0.035
const BOB_SECONDS = 4.2

const TAG = { width: 0.44, height: 0.13 } as const
const TAG_CANVAS = { width: 256, height: 76 } as const

/**
 * A player's colour, from their id.
 *
 * Derived rather than stored, so **everybody in the room sees the same person in the same colour**
 * without a colour ever going on the wire. A hue from a hash also means two players are rarely
 * confusable and nobody had to choose, which is one less thing in a profile to moderate.
 */
export function avatarHue(participantId: string): number {
  let hash = 2166136261
  for (let i = 0; i < participantId.length; i++) {
    hash ^= participantId.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  // >>> 0 first: `imul` returns a signed 32-bit number, and a negative modulo would put a third of
  // all players on a negative hue, which Three reads as a different colour entirely.
  return ((hash >>> 0) % 360) / 360
}

interface Parts {
  readonly group: Group
  readonly materials: Material[]
  readonly textures: CanvasTexture[]
  /**
   * Whether this was built as a body or as an orb (M8-06).
   *
   * Kept because the cache is keyed by *participant* and a participant's shape can change: a
   * player who takes their headset off is a head and two hands that has to become an orb. Without
   * it the first sighting decided the shape for the rest of the session, so somebody who joined
   * from a browser and then entered immersive stayed a floating orb with their hands nowhere — a
   * latent defect since M8-04 that only a kind change reveals.
   *
   * **A shape and not a kind**, because `webxr` and `quest-native` are the same avatar. Storing
   * the kind meant a player moving from the browser's headset mode to the native client threw
   * away four meshes, two materials and a texture to build an identical avatar.
   */
  readonly builtEmbodied: boolean
  /** Set every frame from the participant's row and pose. */
  readonly show: (row: VenuePresence, bay: Bay, drawn: DrawnPose | undefined, seconds: number) => void
}

export function buildAvatars(bays: readonly Bay[]): AvatarsBuild {
  const group = new Group()

  // **Geometry is shared and materials are not.** Six avatars are six colours and six opacities,
  // which is per-instance state, but they are all the same shapes — and a head geometry per player
  // is five uploads of a sphere on a device whose whole frame budget is 11.1 ms.
  const headGeometry = new SphereGeometry(HEAD_RADIUS, 12, 8)
  const visorGeometry = new BoxGeometry(0.155, 0.055, 0.02)
  const wristGeometry = new BoxGeometry(0.085, 0.032, 0.105)
  const orbGeometry = new IcosahedronGeometry(ORB_RADIUS, 1)
  const tagGeometry = new PlaneGeometry(TAG.width, TAG.height)
  const shared: BufferGeometry[] = [
    headGeometry,
    visorGeometry,
    wristGeometry,
    orbGeometry,
    tagGeometry,
  ]

  const parts = new Map<string, Parts>()

  function partsFor(row: VenuePresence): Parts {
    const existing = parts.get(row.participantId)
    // **Rebuilt when the shape changes, not merely when the player is new.** By the time a row
    // reaches here its kind has already been held steady for four seconds by `PlatformSwitch`, so
    // this runs on a real change of platform and not on a headset menu opening.
    if (existing !== undefined && existing.builtEmbodied === isEmbodied(row.clientKind)) {
      return existing
    }
    if (existing !== undefined) forget(row.participantId)
    const made =
      row.clientKind === 'web-windowed'
        ? buildOrb(row, orbGeometry, tagGeometry)
        : buildHeadAndHands(row, headGeometry, visorGeometry, wristGeometry)
    group.add(made.group)
    parts.set(row.participantId, made)
    return made
  }

  function forget(participantId: string): void {
    const going = parts.get(participantId)
    if (going === undefined) return
    group.remove(going.group)
    for (const m of going.materials) m.dispose()
    for (const t of going.textures) t.dispose()
    parts.delete(participantId)
  }

  const update = (port: VenuePort | undefined, seconds: number): void => {
    if (port === undefined) {
      // Not merely hidden — forgotten. A port that goes away is a player who signed out or a
      // connection that closed, and keeping six sets of materials alive against the chance it
      // comes back is a leak with a story attached.
      for (const id of [...parts.keys()]) forget(id)
      return
    }

    const rows = port.others()
    const drawn = port.drawn()
    const seen = new Set<string>()

    for (const row of rows) {
      // **A player whose pose frame this build does not know keeps their place in the room and
      // gets no avatar** (M8-03). Drawing them would mean guessing at a convention, and a guess
      // that was wrong by a handedness puts them mirrored through the cabinet with nothing
      // failing anywhere.
      if (row.clientKind !== 'web-windowed' && !posesAreDrawable(row)) continue

      const bayIndex = port.bayOf(row.participantId)
      // `null` is the arcade being full — present, standing at no machine (M8-01). There is no
      // sixth-and-a-half bay to draw them at, and putting them on somebody else's would be worse
      // than leaving them out of the picture.
      if (bayIndex === undefined || bayIndex === null) continue
      const bay = bays[bayIndex]
      if (bay === undefined) continue

      const pose = drawn.get(row.participantId)
      // A headset player with no pose yet — just joined, or faded out past a second — is not drawn
      // at all. `drawn` has already applied the fade, so there is no opacity to second-guess here.
      if (row.clientKind !== 'web-windowed' && pose === undefined) continue

      seen.add(row.participantId)
      partsFor(row).show(row, bay, pose, seconds)
    }

    for (const id of [...parts.keys()]) {
      if (!seen.has(id)) forget(id)
    }
  }

  return {
    group,
    geometries: shared,
    // Materials and textures are per-player and owned by the parts, which dispose them as players
    // come and go. What is listed here is what the caller must dispose, and it is only the shared
    // geometry — anything else would be disposed twice.
    materials: [],
    textures: [],
    update,
    dispose: () => {
      for (const id of [...parts.keys()]) forget(id)
      for (const g of shared) g.dispose()
    },
  }
}

/** Where a bay's machine stands, in the form the frame conversion wants. */
function placementOf(bay: Bay): { x: number; y: number; z: number; yaw: number } {
  return { x: bay.x, y: 0, z: bay.z, yaw: bay.yaw }
}

function buildHeadAndHands(
  row: VenuePresence,
  headGeometry: BufferGeometry,
  visorGeometry: BufferGeometry,
  wristGeometry: BufferGeometry,
): Parts {
  const hue = avatarHue(row.participantId)
  const skin = new MeshBasicMaterial({
    color: new Color().setHSL(hue, 0.55, 0.42),
    transparent: true,
    // Unlit and untone-mapped, deliberately. The arcade is dark by design (06 §4) and a lit
    // avatar in it is a silhouette; the point of another player being there is that you can see
    // them.
    toneMapped: false,
  })
  const visorSkin = new MeshBasicMaterial({
    color: new Color().setHSL(hue, 0.9, 0.62),
    transparent: true,
    toneMapped: false,
  })

  const group = new Group()
  const head = new Mesh(headGeometry, skin)
  // A little longer front-to-back than side-to-side, which is what a head is and what stops the
  // visor reading as a stripe painted on a ball.
  head.scale.set(1, 1, 1.18)
  const visor = new Mesh(visorGeometry, visorSkin)
  // On the front face. `Placement`'s convention has the front at local +Z.
  visor.position.set(0, 0.012, HEAD_RADIUS * 1.05)
  head.add(visor)

  const left = new Mesh(wristGeometry, skin)
  const right = new Mesh(wristGeometry, skin)
  group.add(head, left, right)

  const show = (
    _row: VenuePresence,
    bay: Bay,
    drawn: DrawnPose | undefined,
    _seconds: number,
  ): void => {
    if (drawn === undefined) {
      group.visible = false
      return
    }
    group.visible = true
    skin.opacity = drawn.opacity
    visorSkin.opacity = drawn.opacity
    place(head, drawn.head, bay)
    left.visible = drawn.leftWrist !== null
    if (drawn.leftWrist !== null) place(left, drawn.leftWrist, bay)
    right.visible = drawn.rightWrist !== null
    if (drawn.rightWrist !== null) place(right, drawn.rightWrist, bay)
  }

  return { group, materials: [skin, visorSkin], textures: [], show, builtEmbodied: true }
}

/**
 * Put one part where a playfield-local pose says, at this bay.
 *
 * The conversion is the same one both clients run against shared fixtures (M8-02), which is what
 * makes a remote player's hands land on *the machine* rather than in the middle of the room.
 */
function place(mesh: Mesh, pose: AvatarPose, bay: Bay): void {
  const at = poseInVenue(pose, placementOf(bay))
  mesh.position.set(at.x, at.y, at.z)
  // YXZ, and the order matters: yaw about world Y, then pitch, then roll, which is how a head
  // turns. The default XYZ would tilt the axis a head yaws about and make a look-down-and-left
  // read as a lean.
  mesh.rotation.order = 'YXZ'
  mesh.rotation.set(at.pitch, at.yaw, at.roll)
}

function buildOrb(
  row: VenuePresence,
  orbGeometry: BufferGeometry,
  tagGeometry: BufferGeometry,
): Parts {
  const hue = avatarHue(row.participantId)
  const glow = new MeshBasicMaterial({
    color: new Color().setHSL(hue, 0.85, 0.6),
    transparent: true,
    // Additive, so the orb reads as a light in a dark room rather than as a painted ball. It is
    // standing in for a person who cannot be seen, and a light is the honest shape for that.
    blending: AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  })

  const group = new Group()
  const orb = new Mesh(orbGeometry, glow)
  group.add(orb)

  const tag = nametag(row.handle, hue, tagGeometry)
  group.add(tag.mesh)

  const show = (
    _row: VenuePresence,
    bay: Bay,
    _drawn: DrawnPose | undefined,
    seconds: number,
  ): void => {
    group.visible = true
    // At the bay's standing position: this is where the player would be if they were here in the
    // room, and it is the one place in the arcade that means "this machine is taken".
    group.position.set(bay.standX, 0, bay.standZ)
    // **A slow bob, and it is not decoration.** A perfectly still orb is indistinguishable from a
    // prop; a player watching from a browser is *present*, and a room that shows no sign of it
    // reads as a room with a lamp in it.
    orb.position.set(0, ORB_HEIGHT + BOB * Math.sin((seconds / BOB_SECONDS) * Math.PI * 2), 0)
    tag.mesh.position.set(0, ORB_HEIGHT + TAG_HEIGHT, 0)
    // The tag faces the way the bay faces, which is where the player at the next machine is
    // standing. Billboarding it to the viewer was the alternative and is worse in a room where
    // everybody stands in a row: six tags all swivelling as you turn your head is a room that
    // will not hold still.
    tag.mesh.rotation.set(0, bay.yaw + Math.PI, 0)
  }

  return {
    group, materials: [glow, tag.material], textures: tag.textures, show, builtEmbodied: false,
  }
}

/**
 * A handle, painted once.
 *
 * Drawn with the browser's own UI face rather than the generated stroke font, which is the same
 * choice `wristPanel.ts` made and for the same reason: the stroke font is the *machine's*
 * typography — it belongs on a playfield and a backglass — and a nametag is chrome. It is also
 * painted exactly once per player, so nothing here runs per frame.
 */
function nametag(
  handle: string,
  hue: number,
  geometry: BufferGeometry,
): { mesh: Mesh; material: Material; textures: CanvasTexture[] } {
  const host = (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } })
    .document
  const canvas = host?.createElement('canvas')
  const ctx = canvas?.getContext('2d') ?? null
  let texture: CanvasTexture | undefined
  if (canvas !== undefined && ctx !== null) {
    canvas.width = TAG_CANVAS.width
    canvas.height = TAG_CANVAS.height
    paintNametag(ctx, handle, hue, canvas.width, canvas.height)
    texture = new CanvasTexture(canvas)
    texture.colorSpace = SRGBColorSpace
    texture.generateMipmaps = false
    texture.minFilter = LinearFilter
  }

  const material = new MeshBasicMaterial({
    ...(texture === undefined ? { color: 0x101018 } : { map: texture }),
    transparent: true,
    toneMapped: false,
  })
  const mesh = new Mesh(geometry, material)
  return { mesh, material, textures: texture === undefined ? [] : [texture] }
}

function paintNametag(
  ctx: CanvasRenderingContext2D,
  handle: string,
  hue: number,
  width: number,
  height: number,
): void {
  ctx.clearRect(0, 0, width, height)
  const ink = new Color().setHSL(hue, 0.85, 0.72).getStyle()

  // A dark plate behind the type. Without it the handle is unreadable against a lit backglass,
  // which is precisely what is behind every bay in this room.
  ctx.fillStyle = 'rgba(6, 6, 12, 0.72)'
  roundedRect(ctx, 2, 2, width - 4, height - 4, 14)
  ctx.fill()
  ctx.strokeStyle = ink
  ctx.lineWidth = 2
  ctx.stroke()

  ctx.font = '600 34px system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = ink
  // Clipped rather than shrunk. A handle long enough to overflow is rare, and type that changes
  // size per player makes a row of tags look like a ransom note.
  ctx.save()
  roundedRect(ctx, 10, 6, width - 20, height - 12, 10)
  ctx.clip()
  ctx.fillText(handle, width / 2, height / 2 + 1)
  ctx.restore()
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}
